import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Utensils, Clock, CheckCircle, Users } from 'lucide-react';
import { useTables } from './hooks/useTables';
import { TableMap, TableManagement, TableForm } from './components';
import { Table, Order, TableStatus, TableFormData } from './types';
import { mockOrders } from './mocks/tables';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

export const TablesPage: React.FC = () => {
  const {
    tables,
    stats,
    loading,
    error,
    filters,
    setFilters,
    createTable,
    updateTable,
    deleteTable,
    updateTableStatus
  } = useTables();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [isManagementOpen, setIsManagementOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<Table | null>(null);

  const handleTableClick = (table: Table) => {
    setSelectedTable(table);
    setIsManagementOpen(true);
  };

  const handleUpdateTableStatus = async (tableId: string, status: TableStatus) => {
    try {
      await updateTableStatus(tableId, status);
      setIsManagementOpen(false);
      setSelectedTable(null);
    } catch (error) {
      console.error('Error updating table status:', error);
    }
  };

  const handleSplitBill = (tableId: string) => {
    console.log('Split bill for table:', tableId);
    // Implementar lógica de división de cuenta
  };

  const handleTransferItems = (tableId: string) => {
    console.log('Transfer items from table:', tableId);
    // Implementar lógica de transferencia de productos
  };

  const getTableOrder = (table: Table): Order | undefined => {
    return mockOrders.find(order => order.tableId === table.id);
  };

  const handleCreateTable = async (data: TableFormData) => {
    try {
      await createTable(data);
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating table:', error);
    }
  };

  const handleUpdateTable = async (data: TableFormData) => {
    if (!editingTable) return;
    
    try {
      await updateTable(editingTable.id, data);
      setEditingTable(null);
    } catch (error) {
      console.error('Error updating table:', error);
    }
  };

  const getExistingTableNumbers = (): number[] => {
    return tables.map(table => table.number);
  };

  const handleTableDrag = (tableId: string, position: { x: number; y: number }) => {
    console.log('Table dragged:', tableId, position);
    // Aquí implementaremos la lógica para actualizar la posición de la mesa
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Cargando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Gestión de Mesas</h1>
        <Button 
          className="bg-macartur-primary hover:bg-macartur-accent1"
          onClick={() => setIsCreateModalOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Mesa
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Mesas</CardTitle>
            <Utensils className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalTables || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Mesas Ocupadas</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.occupiedTables || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Mesas Reservadas</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.reservedTables || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Mesas Disponibles</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.availableTables || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tables Map */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-medium">Mapa de Mesas</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditMode(!editMode)}
          >
            {editMode ? 'Guardar Layout' : 'Editar Layout'}
          </Button>
        </CardHeader>
        <CardContent>
          <div data-testid="tables-map">
            <TableMap
              tables={tables}
              onTableClick={handleTableClick}
              onTableDrag={handleTableDrag}
              editMode={editMode}
            />
          </div>
        </CardContent>
      </Card>

      {/* Table Management Modal */}
      {selectedTable && (
        <TableManagement
          table={selectedTable}
          order={getTableOrder(selectedTable)}
          isOpen={isManagementOpen}
          onClose={() => {
            setIsManagementOpen(false);
            setSelectedTable(null);
          }}
          onUpdateStatus={handleUpdateTableStatus}
          onSplitBill={handleSplitBill}
          onTransferItems={handleTransferItems}
        />
      )}

      {/* Create Table Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nueva Mesa</DialogTitle>
          </DialogHeader>
          <TableForm
            existingTableNumbers={getExistingTableNumbers()}
            onSubmit={handleCreateTable}
            onCancel={() => setIsCreateModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Table Modal */}
      <Dialog open={!!editingTable} onOpenChange={() => setEditingTable(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Mesa</DialogTitle>
          </DialogHeader>
          {editingTable && (
            <TableForm
              initialData={{
                number: editingTable.number,
                capacity: editingTable.capacity,
                shape: editingTable.shape,
                position: editingTable.position
              }}
              existingTableNumbers={getExistingTableNumbers()}
              onSubmit={handleUpdateTable}
              onCancel={() => setEditingTable(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};